// File: src/contexts/AuthContext.js
import React, { createContext, useContext, useEffect, useState } from "react";
import api from "../api/client";
import { trustCurrentDevice, untrustCurrentDevice } from "../api/userDevices";
import logger from "../utils/logger.js";

const AuthContext = createContext(null);

const USER_KEY = "mg_user";
const AUTH_TOKEN_KEYS = ["mg_token", "token", "auth_token", "jwt"];

// NOTE: Token is handled via httpOnly cookie from backend
// Not retrieved from localStorage (security best practice)

function persistAuthToken(token) {
  // Store token in localStorage for API header usage
  if (token) {
    try {
      localStorage.setItem("mg_token", token);
      logger.info("Auth token saved to localStorage");
    } catch (e) {
      logger.error("Failed to save auth token:", e);
    }
  }
}

function clearStoredAuth() {
  // Clear any legacy tokens from localStorage (for migration from old code)
  try {
    localStorage.removeItem(USER_KEY);
    AUTH_TOKEN_KEYS.forEach((key) => localStorage.removeItem(key));
    AUTH_TOKEN_KEYS.forEach((key) => sessionStorage.removeItem(key));
  } catch {}

  // Token is in httpOnly cookie - backend will clear it via Set-Cookie with Max-Age=0
}

function resolveVerified(user) {
  if (!user) return true;

  const value =
    user.email_verified_at ??
    user.is_verified ??
    user.email_verified ??
    user.verified ??
    null;

  if (value === null || value === undefined) return false;

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (
      normalized === "1" ||
      normalized === "true" ||
      normalized === "yes" ||
      normalized === "verified"
    ) {
      return true;
    }
    if (
      normalized === "0" ||
      normalized === "false" ||
      normalized === "no" ||
      normalized === ""
    ) {
      return false;
    }
  }

  return !!value;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isVerified, setIsVerified] = useState(true);

  function clearAuth() {
    clearStoredAuth();
    setUser(null);
    setIsVerified(true);
  }

  function applyUser(u) {
    const nextUser = u || null;
    setUser(nextUser);
    setIsVerified(resolveVerified(nextUser));

    // DO NOT store user object in localStorage (sensitive data exposure risk)
    // User data is fetched fresh from /me endpoint on app load
    try {
      localStorage.removeItem(USER_KEY);
    } catch {}
  }

  async function fetchAndApplyProfile(authToken = "") {
    try {
      const data = await api.apiGet("me", authToken ? { authToken } : {});

      if (!data) {
        logger.error("No data returned from /me endpoint");
        return { ok: false };
      }

      const user = data?.user || data?.data || data;

      if (!user || !user.id) {
        logger.error("Invalid user data from /me endpoint", data);
        return { ok: false };
      }

      applyUser(user);
      return { ok: true, user };
    } catch (err) {
      logger.error("Failed to fetch profile from /me endpoint", err);
      return { ok: false };
    }
  }

  /* ================== INIT ================== */
  useEffect(() => {
    async function init() {
      try {
        // Always fetch fresh user data from /me endpoint
        // Don't rely on cached localStorage (security best practice)
        const profileResult = await fetchAndApplyProfile();
        if (!profileResult.ok) {
          clearAuth();
        }
      } catch (err) {
        logger.error("Auth init error:", err);
        if (err?.status === 401 || err?.status === 403) {
          clearAuth();
        }
      } finally {
        setLoading(false);
      }
    }

    init();
  }, []);

  /* ================== HEARTBEAT ================== */
  useEffect(() => {
    if (!user) return;

    let stopped = false;

    async function heartbeat() {
      try {
        const data = await api.apiGet("me");

        if (!data || !data.user) return;

        if (!stopped) {
          applyUser(data.user);
        }
      } catch (err) {
        if (err?.status === 401 || err?.status === 403) {
          if (!stopped) clearAuth();
        }
      }
    }

    const intervalId = setInterval(heartbeat, 7 * 60 * 1000);

    return () => {
      stopped = true;
      clearInterval(intervalId);
    };
  }, [user]);

  /* ================== RESEND VERIFICATION ================== */
  async function resendVerification() {
    return { ok: false, message: "Not supported yet in Laravel API" };
  }

  /* ================== LOGIN ================== */
  async function login(email, password, options = {}) {
    try {
      const loginResponse = await api.apiPostJson("login", { email, password });

      persistAuthToken(loginResponse?.token);

      try {
        const profileResult = await fetchAndApplyProfile(loginResponse?.token || "");
        if (profileResult.ok) {
          if (options?.trustDevice === true) {
            try {
              await trustCurrentDevice();
              const refreshedProfile = await fetchAndApplyProfile(loginResponse?.token || "");
              if (refreshedProfile.ok) {
                return { ok: true, user: refreshedProfile.user };
              }
            } catch (deviceErr) {
              logger.warn("Trusted device registration failed", deviceErr);
            }
          } else if (options?.trustDevice === false) {
            try {
              await untrustCurrentDevice();
              const refreshedProfile = await fetchAndApplyProfile(loginResponse?.token || "");
              if (refreshedProfile.ok) {
                return { ok: true, user: refreshedProfile.user };
              }
            } catch (deviceErr) {
              logger.warn("Trusted device removal failed", deviceErr);
            }
          }

          return { ok: true, user: profileResult.user };
        }
      } catch (profileErr) {
        logger.warn(
          "Profile fetch after login failed",
          profileErr
        );
      }

      const fallbackUser = loginResponse?.user || null;
      if (fallbackUser?.id) {
        logger.warn("Using login response user as profile fallback");
        applyUser(fallbackUser);
        return { ok: true, user: fallbackUser };
      }

      clearAuth();
      return { ok: false, message: "Could not load profile after login." };
    } catch (err) {
      logger.error("Login error:", err);
      return { ok: false, message: err?.message || "Error while logging in" };
    }
  }

  /* ================== REGISTER ================== */
  async function register(input, email, password) {
    try {
      const payload =
        input && typeof input === "object"
          ? input
          : {
              name: input,
              email,
              password,
            };

      const data = await api.apiPostJson("register", payload);

      if (!data) {
        return { ok: false, message: "Register failed" };
      }

      persistAuthToken(data?.token);

      try {
        const profileResult = await fetchAndApplyProfile(data?.token || "");
        if (profileResult.ok) {
          return { ok: true, user: profileResult.user };
        }
      } catch (profileErr) {
        logger.warn(
          "Profile fetch after register failed",
          profileErr
        );
      }

      const fallbackUser = data?.user || null;
      if (fallbackUser?.id) {
        logger.warn("Using register response user as profile fallback");
        applyUser(fallbackUser);
        return { ok: true, user: fallbackUser };
      }

      clearAuth();
      return { ok: false, message: "Could not load profile after register." };
    } catch (err) {
      logger.error("Register error:", err);
      return { ok: false, message: err?.message || "Error while registering" };
    }
  }

  async function logout() {
    try {
      if (user) {
        await api.apiPostJson("logout", {});
      }
    } catch (err) {
      logger.warn("Logout request failed:", err);
    } finally {
      clearAuth();
    }
  }

  const value = {
    user,
    isLogged: !!user,
    isVerified,
    loading,
    login,
    register,
    logout,
    resendVerification,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
